import { match, P } from 'ts-pattern'
import {
  type AiScanRunState,
  type BeaconEffort,
  type BeaconProgress,
  type BeaconRun,
  type BeaconStage,
  type BeaconSummary,
  type CoverageVerdict,
  type CoverageVerdictLevel,
} from '../../utils/api-client/api-schemas.ts'
import {
  type BeaconAssetRef,
  type BeaconCandidateRef,
  type BeaconControlRef,
  type BeaconEntryPointRef,
  type BeaconEvent,
  type BeaconInvestigationRef,
  type BeaconOperationRef,
  type BeaconStreamFrame,
  parseBeaconEvent,
} from '../../utils/api-client/sse-schemas.ts'

/**
 * One Beacon run as the live view sees it, rebuilt from the run's event stream alone.
 *
 * `fold` applies one event and `replay` folds a prefix of the stream from an empty run, so the same
 * prefix always yields the same state: every viewer of a run, and a viewer who reloads it, lands on
 * the same picture. Nothing here reads the clock or keeps client-only state.
 */

export const DETECTION_LENSES = [
  'ai_scan_surface_map',
  'ai_scan_access',
  'ai_scan_input_forward',
  'ai_scan_sink_backward',
  'ai_scan_control_audit',
  'ai_scan_hygiene',
] as const
export type DetectionLens = (typeof DETECTION_LENSES)[number]

export const LENS_LABELS: Record<string, string> = {
  ai_scan_surface_map: 'Surface map',
  ai_scan_access: 'Access',
  ai_scan_input_forward: 'Input-forward',
  ai_scan_sink_backward: 'Sink-backward',
  ai_scan_control_audit: 'Control audit',
  ai_scan_hygiene: 'Hygiene',
  ai_scan_verify: 'Verify',
}

export const lensLabel = (lens: string): string => LENS_LABELS[lens] ?? lens

export const STAGES: readonly { stage: BeaconStage; label: string }[] = [
  { stage: 'threat_model', label: 'Threat model' },
  { stage: 'surface_map', label: 'Surface map' },
  { stage: 'investigate', label: 'Investigate' },
  { stage: 'verify', label: 'Verify & triage' },
]

export type RunStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
export type StageProgress = { state: 'pending' | 'running' | 'done'; start?: string; end?: string }
export type EntryPointMapping = 'pending' | 'mapping' | 'mapped' | 'empty' | 'incomplete'

export type EntryPointNode = BeaconEntryPointRef & {
  mapping: EntryPointMapping
  reason: string
  opIds: string[]
  groupKeys: string[]
  active: number
  lenses: string[]
  candidates: string[]
}

export type AssetNode = BeaconAssetRef & { opIds: string[] }

export type ControlState = 'pending' | 'planned' | 'auditing' | 'audited' | 'n/a'
export type ControlNode = BeaconControlRef & {
  state: ControlState
  active: number
  lenses: string[]
  candidates: string[]
}

export type OperationNode = BeaconOperationRef & {
  entryPoint: string
  groupKey: string
  active: number
  isExamined: boolean
  lenses: string[]
  candidates: string[]
}

export type GroupNode = {
  key: string
  entryPoint: string
  name: string
  opIds: string[]
  assetIds: string[]
  candidates: string[]
}

export type LensState = 'pending' | 'running' | 'done' | 'skipped'
export type LensNode = {
  state: LensState
  planned: number
  run: number
  spent: number
  allotted: number
  reason: string
}

export type InvestigationNode = BeaconInvestigationRef & {
  state: 'planned' | 'running' | 'done'
  candidates: string[]
  files?: number
  tokens?: number
}

/** Where a candidate sits in the picture: the nearest operation by file and line, else what raised it. */
export type Placement =
  | { kind: 'op'; key: string }
  | { kind: 'group'; key: string }
  | { kind: 'control'; key: string }
  | { kind: 'ep'; key: string }
  | { kind: 'repo'; key: 'repository' }

export type CandidateState = 'suspected' | 'upheld' | 'refuted' | 'kept' | 'dropped' | 'merged'

export type CandidateNode = BeaconCandidateRef & {
  lens: string
  investigation: string
  at: Placement
  state: CandidateState
  refutedOn: string[]
  result?: string
}

export type LogKind = 'stage' | 'lens' | 'cand' | 'kept' | 'gone' | 'ok' | 'plain'
export type LogEntry = { seq: number; ts: string; kind: LogKind; message: string; isFindingEvent: boolean }

export type BeaconRunState = {
  seq: number
  ts?: string
  startTs?: string
  status: RunStatus
  /** The scan status the latest frame was recorded under (queued, fetching, generating, triaging, …). */
  phase: string
  isCancelRequested: boolean
  failureMessage?: string
  effort?: BeaconEffort
  budget: number
  spent: number
  byLens: Record<string, number>
  stages: Record<BeaconStage, StageProgress>
  entryPoints: Record<string, EntryPointNode>
  entryPointOrder: string[]
  assets: Record<string, AssetNode>
  assetOrder: string[]
  controls: Record<string, ControlNode>
  controlOrder: string[]
  operations: Record<string, OperationNode>
  groups: Record<string, GroupNode>
  groupOrder: string[]
  lenses: Record<DetectionLens, LensNode>
  lensesDone: number
  investigations: Record<string, InvestigationNode>
  investigationOrder: string[]
  investigationsDone: number
  candidates: Record<string, CandidateNode>
  candidateOrder: string[]
  candidatesRaw: number
  deduped?: number
  /** Candidates the verifier has ruled on, upheld or refuted. */
  verified: number
  /** Candidates triage has ruled on, which is how analysis-service counts verification's progress. */
  triaged: number
  triage: { processed: number; total: number }
  sinks?: { count: number; error: string | null }
  fileReads: number
  verdict?: CoverageVerdict
  examinedPercent?: number
  repo: { state: 'pending' | 'running' | 'done'; candidates: string[] }
  log: LogEntry[]
}

const emptyLens = (): LensNode => ({ state: 'pending', planned: 0, run: 0, spent: 0, allotted: 0, reason: '' })

export const EMPTY_BEACON_RUN: BeaconRunState = {
  seq: 0,
  status: 'pending',
  phase: 'queued',
  isCancelRequested: false,
  budget: 0,
  spent: 0,
  byLens: {},
  stages: {
    threat_model: { state: 'pending' },
    surface_map: { state: 'pending' },
    investigate: { state: 'pending' },
    verify: { state: 'pending' },
  },
  entryPoints: {},
  entryPointOrder: [],
  assets: {},
  assetOrder: [],
  controls: {},
  controlOrder: [],
  operations: {},
  groups: {},
  groupOrder: [],
  lenses: {
    ai_scan_surface_map: emptyLens(),
    ai_scan_access: emptyLens(),
    ai_scan_input_forward: emptyLens(),
    ai_scan_sink_backward: emptyLens(),
    ai_scan_control_audit: emptyLens(),
    ai_scan_hygiene: emptyLens(),
  },
  lensesDone: 0,
  investigations: {},
  investigationOrder: [],
  investigationsDone: 0,
  candidates: {},
  candidateOrder: [],
  candidatesRaw: 0,
  verified: 0,
  triaged: 0,
  triage: { processed: 0, total: 0 },
  fileReads: 0,
  repo: { state: 'pending', candidates: [] },
  log: [],
}

const isDetectionLens = (lens: string): lens is DetectionLens => (DETECTION_LENSES as readonly string[]).includes(lens)

const updateEntry = <T>(record: Record<string, T>, key: string, update: (value: T) => T): Record<string, T> => {
  const value = record[key]
  return value === undefined ? record : { ...record, [key]: update(value) }
}

const withLens = (lenses: string[], lens: string): string[] => (lenses.includes(lens) ? lenses : [...lenses, lens])

/** The line an operation's location gives for `file`, from locations like `a.go:12; b.go:40`. */
export const operationLine = (operation: Pick<BeaconOperationRef, 'location'>, file: string): number | undefined => {
  const hit = operation.location
    .split(';')
    .map(part => part.trim())
    .find(part => part.startsWith(`${file}:`))
  const line = hit ? Number.parseInt(hit.slice(file.length + 1), 10) : Number.NaN
  return Number.isNaN(line) ? undefined : line
}

const placeCandidate = (state: BeaconRunState, inv: BeaconInvestigationRef, cand: BeaconCandidateRef): Placement => {
  const targets = inv.targets
  const nearest = targets.operation_ids
    .flatMap(id => {
      const operation = state.operations[id]
      const line = operation ? operationLine(operation, cand.file_path) : undefined
      return operation && line !== undefined ? [{ id, distance: Math.abs(line - cand.line) }] : []
    })
    .sort((a, b) => a.distance - b.distance)[0]
  if (nearest) {
    return { kind: 'op', key: nearest.id }
  }
  const firstOperation = targets.operation_ids[0]
  const group = firstOperation ? state.operations[firstOperation]?.groupKey : undefined
  if (inv.focus_kind === 'operation_group' && group) {
    return { kind: 'group', key: group }
  }
  const control = targets.control_ids[0]
  if (control) {
    return { kind: 'control', key: control }
  }
  const entryPoint = targets.entry_point_ids[0]
  return entryPoint ? { kind: 'ep', key: entryPoint } : { kind: 'repo', key: 'repository' }
}

const holdCandidate = (state: BeaconRunState, at: Placement, uuid: string): BeaconRunState => {
  const add = <T extends { candidates: string[] }>(holder: T): T => ({
    ...holder,
    candidates: [...holder.candidates, uuid],
  })
  return match(at)
    .with({ kind: 'op' }, ({ key }) => ({ ...state, operations: updateEntry(state.operations, key, add) }))
    .with({ kind: 'group' }, ({ key }) => ({ ...state, groups: updateEntry(state.groups, key, add) }))
    .with({ kind: 'control' }, ({ key }) => ({ ...state, controls: updateEntry(state.controls, key, add) }))
    .with({ kind: 'ep' }, ({ key }) => ({ ...state, entryPoints: updateEntry(state.entryPoints, key, add) }))
    .with({ kind: 'repo' }, () => ({ ...state, repo: add(state.repo) }))
    .exhaustive()
}

/**
 * Applies `touch` to everything an investigation examines. Only operation-group investigations examine
 * individual operations; an entry-point (access) investigation reviews the entry point as a whole, so it
 * lights the zone, not every operation in it.
 */
const touchTargets = (
  state: BeaconRunState,
  inv: BeaconInvestigationRef,
  touch: {
    operation: (operation: OperationNode) => OperationNode
    entryPoint: (entryPoint: EntryPointNode) => EntryPointNode
    control: (control: ControlNode) => ControlNode
  }
): BeaconRunState => {
  const { targets } = inv
  const operations =
    inv.focus_kind === 'operation_group'
      ? targets.operation_ids.reduce((acc, id) => updateEntry(acc, id, touch.operation), state.operations)
      : state.operations
  return {
    ...state,
    operations,
    entryPoints: targets.entry_point_ids.reduce((acc, id) => updateEntry(acc, id, touch.entryPoint), state.entryPoints),
    controls: targets.control_ids.reduce((acc, id) => updateEntry(acc, id, touch.control), state.controls),
  }
}

const logKind = (event: BeaconEvent): LogKind =>
  match(event)
    .returnType<LogKind>()
    .with(
      { name: P.union('stage_started', 'stage_finished', 'completed', 'summary_ready', 'cancelled', 'failed') },
      () => 'stage'
    )
    .with({ name: P.union('lens_finished', 'plan_built') }, () => 'lens')
    .with({ name: 'investigation_finished' }, ({ detail }) => (detail.candidates.length > 0 ? 'cand' : 'ok'))
    .with({ name: 'candidate_verified' }, ({ detail }) => (detail.verdict === 'refuted' ? 'gone' : 'cand'))
    .with({ name: 'candidate_triaged' }, ({ detail }) => (detail.kept ? 'kept' : 'gone'))
    .otherwise(() => 'plain')

const isFindingEvent = (event: BeaconEvent): boolean =>
  event.name === 'candidate_triaged' ||
  event.name === 'candidate_verified' ||
  (event.name === 'investigation_finished' && event.detail.candidates.length > 0)

// Progress ticks and "started mapping" frames are the beat of the run, not news; the narrative skips them.
const QUIET_EVENTS = new Set<BeaconEvent['name']>(['findings_progress', 'entry_point_mapping_started'])

const applyEntryPointMapped = (
  state: BeaconRunState,
  detail: Extract<BeaconEvent, { name: 'entry_point_mapped' }>['detail']
): BeaconRunState => {
  const ep = state.entryPoints[detail.entry_point_id]
  if (!ep) {
    return state
  }
  const fresh = detail.operations.filter(operation => !state.operations[operation.id])
  const groups = { ...state.groups }
  const groupOrder = [...state.groupOrder]
  const groupKeys = [...ep.groupKeys]
  const assets = { ...state.assets }
  const operations = { ...state.operations }
  for (const operation of fresh) {
    const groupKey = `${ep.id}::${operation.group}`
    const group = groups[groupKey] ?? {
      key: groupKey,
      entryPoint: ep.id,
      name: operation.group,
      opIds: [],
      assetIds: [],
      candidates: [],
    }
    if (!groups[groupKey]) {
      groupOrder.push(groupKey)
      groupKeys.push(groupKey)
    }
    groups[groupKey] = {
      ...group,
      opIds: [...group.opIds, operation.id],
      assetIds: [...group.assetIds, ...operation.asset_ids.filter(asset => !group.assetIds.includes(asset))],
    }
    for (const assetId of operation.asset_ids) {
      const asset = assets[assetId]
      if (asset) {
        assets[assetId] = { ...asset, opIds: [...asset.opIds, operation.id] }
      }
    }
    operations[operation.id] = {
      ...operation,
      entryPoint: ep.id,
      groupKey,
      active: 0,
      isExamined: false,
      lenses: [],
      candidates: [],
    }
  }
  return {
    ...state,
    groups,
    groupOrder,
    assets,
    operations,
    entryPoints: {
      ...state.entryPoints,
      [ep.id]: {
        ...ep,
        mapping: detail.outcome,
        reason: detail.reason,
        groupKeys,
        opIds: [...ep.opIds, ...fresh.map(operation => operation.id)],
      },
    },
  }
}

const applyInvestigationFinished = (
  state: BeaconRunState,
  detail: Extract<BeaconEvent, { name: 'investigation_finished' }>['detail']
): BeaconRunState => {
  const inv = state.investigations[detail.investigation_id]
  if (!inv) {
    return state
  }
  // Only an investigation that ran to completion examined its targets. One the budget skipped never
  // started, so it has nothing to stand down either.
  const isCompleted = detail.outcome === undefined || detail.outcome === 'completed'
  const release = inv.state === 'running' ? 1 : 0
  const touched = touchTargets(state, inv, {
    operation: operation => ({
      ...operation,
      active: Math.max(0, operation.active - release),
      lenses: isCompleted ? withLens(operation.lenses, inv.lens) : operation.lenses,
      isExamined: operation.isExamined || isCompleted,
    }),
    entryPoint: ep => ({
      ...ep,
      active: Math.max(0, ep.active - release),
      lenses: isCompleted ? withLens(ep.lenses, inv.lens) : ep.lenses,
    }),
    control: control => ({
      ...control,
      active: Math.max(0, control.active - release),
      lenses: isCompleted ? withLens(control.lenses, inv.lens) : control.lenses,
      state: match({ isCompleted, isStillActive: control.active - release > 0, prior: control.state })
        .returnType<ControlState>()
        .with({ isCompleted: true }, () => 'audited')
        .with({ isStillActive: true }, () => 'auditing')
        .with({ prior: 'auditing' }, () => 'planned')
        .with({ prior: P.union('pending', 'planned', 'audited', 'n/a') }, ({ prior }) => prior)
        .exhaustive(),
    }),
  })
  const base: BeaconRunState = {
    ...touched,
    investigationsDone: state.investigationsDone + 1,
    fileReads: state.fileReads + detail.files_read,
    candidatesRaw: state.candidatesRaw + detail.candidates.length,
    repo: inv.focus_kind === 'repo' && isCompleted ? { ...touched.repo, state: 'done' } : touched.repo,
    investigations: {
      ...state.investigations,
      [inv.investigation_id]: {
        ...inv,
        state: 'done',
        files: detail.files_read,
        tokens: detail.tokens_spent,
        candidates: [...inv.candidates, ...detail.candidates.map(cand => cand.finding_uuid)],
      },
    },
  }
  return detail.candidates.reduce<BeaconRunState>((acc, cand) => {
    if (acc.candidates[cand.finding_uuid]) {
      return acc
    }
    const at = placeCandidate(acc, inv, cand)
    const node: CandidateNode = {
      ...cand,
      lens: inv.lens,
      investigation: inv.investigation_id,
      at,
      state: 'suspected',
      refutedOn: [],
    }
    return holdCandidate(
      {
        ...acc,
        candidates: { ...acc.candidates, [cand.finding_uuid]: node },
        candidateOrder: [...acc.candidateOrder, cand.finding_uuid],
      },
      at,
      cand.finding_uuid
    )
  }, base)
}

const addEntryPoints = (state: BeaconRunState, entryPoints: readonly BeaconEntryPointRef[]): BeaconRunState => {
  const fresh = entryPoints.filter(ep => !state.entryPoints[ep.id])
  return {
    ...state,
    entryPoints: {
      ...state.entryPoints,
      ...Object.fromEntries(
        fresh.map(ep => [
          ep.id,
          {
            ...ep,
            mapping: 'pending' as const,
            reason: '',
            opIds: [],
            groupKeys: [],
            active: 0,
            lenses: [],
            candidates: [],
          },
        ])
      ),
    },
    entryPointOrder: [...state.entryPointOrder, ...fresh.map(ep => ep.id)],
  }
}

const applyEvent = (state: BeaconRunState, event: BeaconEvent): BeaconRunState =>
  match(event)
    .with({ name: 'scan_configured' }, ({ detail }) => ({
      ...state,
      status: 'running' as const,
      effort: detail.effort,
      budget: detail.token_budget,
    }))
    .with({ name: 'stage_started' }, ({ detail, ts }) => ({
      ...state,
      stages: { ...state.stages, [detail.stage]: { state: 'running' as const, start: ts } },
    }))
    .with({ name: 'stage_finished' }, ({ detail, ts }) => {
      const stages = {
        ...state.stages,
        [detail.stage]: { ...state.stages[detail.stage], state: 'done' as const, end: ts },
      }
      // A candidate still only suspected when verification ends was folded into another one.
      const candidates =
        detail.stage === 'verify'
          ? Object.fromEntries(
              Object.entries(state.candidates).map(([uuid, cand]) => [
                uuid,
                cand.state === 'suspected' ? { ...cand, state: 'merged' as const } : cand,
              ])
            )
          : state.candidates
      return { ...state, stages, candidates }
    })
    .with({ name: 'threat_model_loaded' }, ({ detail }) => ({
      ...addEntryPoints(state, detail.entry_points),
      assets: Object.fromEntries(detail.assets.map(asset => [asset.id, { ...asset, opIds: [] }])),
      assetOrder: detail.assets.map(asset => asset.id),
      controls: Object.fromEntries(
        detail.controls.map(control => [
          control.id,
          {
            ...control,
            state: control.auditable ? ('pending' as const) : ('n/a' as const),
            active: 0,
            lenses: [],
            candidates: [],
          },
        ])
      ),
      controlOrder: detail.controls.map(control => control.id),
    }))
    .with({ name: 'entry_point_mapping_started' }, ({ detail }) => ({
      ...state,
      entryPoints: updateEntry(state.entryPoints, detail.entry_point_id, ep => ({
        ...ep,
        mapping: 'mapping' as const,
      })),
    }))
    .with({ name: 'entry_point_mapped' }, ({ detail }) => applyEntryPointMapped(state, detail))
    .with({ name: 'sinks_inventoried' }, ({ detail }) => ({
      ...state,
      sinks: { count: detail.count, error: detail.error },
    }))
    .with({ name: 'plan_built' }, ({ detail }) => {
      const lenses = { ...state.lenses }
      for (const inv of detail.investigations) {
        if (isDetectionLens(inv.lens)) {
          lenses[inv.lens] = { ...lenses[inv.lens], planned: lenses[inv.lens].planned + 1 }
        }
      }
      const planned = new Set(detail.investigations.flatMap(inv => inv.targets.control_ids))
      return {
        ...state,
        lenses,
        investigations: Object.fromEntries(
          detail.investigations.map(inv => [
            inv.investigation_id,
            { ...inv, state: 'planned' as const, candidates: [] },
          ])
        ),
        investigationOrder: detail.investigations.map(inv => inv.investigation_id),
        controls: Object.fromEntries(
          Object.entries(state.controls).map(([id, control]) => [
            id,
            control.state === 'pending' && planned.has(id) ? { ...control, state: 'planned' as const } : control,
          ])
        ),
      }
    })
    .with({ name: 'lens_finished' }, ({ detail }) => {
      if (!isDetectionLens(detail.lens)) {
        return state
      }
      const isSkipped = detail.planned === 0
      const reason = match({ isSkipped, hasSinkError: !!state.sinks?.error })
        .with({ isSkipped: false }, () => '')
        .with({ hasSinkError: true }, () => 'Sink inventory unavailable — nothing to trace back from.')
        .with({ hasSinkError: false }, () => 'Nothing planned for this lens.')
        .exhaustive()
      return {
        ...state,
        lensesDone: state.lensesDone + 1,
        lenses: {
          ...state.lenses,
          [detail.lens]: {
            ...state.lenses[detail.lens],
            allotted: detail.allotted,
            spent: detail.spent,
            run: detail.run,
            state: isSkipped ? ('skipped' as const) : ('done' as const),
            reason,
          },
        },
      }
    })
    .with({ name: 'investigation_started' }, ({ detail }) => {
      const inv = state.investigations[detail.investigation_id]
      if (!inv) {
        return state
      }
      const touched = touchTargets(state, inv, {
        operation: operation => ({ ...operation, active: operation.active + 1 }),
        entryPoint: ep => ({ ...ep, active: ep.active + 1 }),
        control: control => ({ ...control, active: control.active + 1, state: 'auditing' }),
      })
      return {
        ...touched,
        investigations: { ...state.investigations, [inv.investigation_id]: { ...inv, state: 'running' as const } },
        lenses: isDetectionLens(inv.lens)
          ? { ...state.lenses, [inv.lens]: { ...state.lenses[inv.lens], state: 'running' as const } }
          : state.lenses,
        repo: inv.focus_kind === 'repo' ? { ...state.repo, state: 'running' as const } : state.repo,
      }
    })
    .with({ name: 'investigation_finished' }, ({ detail }) => applyInvestigationFinished(state, detail))
    .with({ name: 'budget_snapshot' }, ({ detail }) => ({ ...state, spent: detail.spent, byLens: detail.by_lens }))
    .with({ name: 'candidates_deduped' }, ({ detail }) => ({
      ...state,
      deduped: detail.deduped,
      triage: { ...state.triage, total: detail.deduped },
    }))
    .with({ name: 'candidate_verified' }, ({ detail }) => ({
      ...state,
      verified: state.verified + 1,
      candidates: updateEntry(state.candidates, detail.finding_uuid, (cand): CandidateNode => ({
        ...cand,
        state: detail.verdict === 'refuted' ? 'refuted' : 'upheld',
        refutedOn: detail.refuted_on,
      })),
    }))
    .with({ name: 'candidate_triaged' }, ({ detail }) => ({
      ...state,
      triaged: state.triaged + 1,
      candidates: updateEntry(state.candidates, detail.finding_uuid, (cand): CandidateNode => ({
        ...cand,
        result: detail.result_type,
        state: detail.kept ? 'kept' : cand.state === 'refuted' ? 'refuted' : 'dropped',
      })),
    }))
    .with({ name: 'findings_progress' }, ({ detail }) =>
      // While investigating the counts are investigations, which the run already tracks itself.
      detail.stage === 'investigate'
        ? state
        : { ...state, triage: { processed: detail.processed, total: detail.total } }
    )
    .with({ name: 'summary_ready' }, ({ detail }) => ({
      ...state,
      verdict: detail.verdict,
      examinedPercent: detail.examined_percent,
    }))
    .with({ name: 'cancel_requested' }, () => ({ ...state, isCancelRequested: true }))
    .with({ name: 'completed' }, () => ({
      ...state,
      status: 'completed' as const,
      // A run without the verify stage (generate mode) keeps every candidate it raised.
      candidates:
        state.stages.verify.state === 'pending'
          ? Object.fromEntries(
              Object.entries(state.candidates).map(([uuid, cand]) => [
                uuid,
                cand.state === 'suspected' ? { ...cand, state: 'kept' as const } : cand,
              ])
            )
          : state.candidates,
    }))
    .with({ name: 'failed' }, ({ message }) => ({ ...state, status: 'failed' as const, failureMessage: message }))
    .with({ name: 'cancelled' }, () => ({ ...state, status: 'cancelled' as const }))
    .with({ name: 'routes_reconciled' }, ({ detail }) => addEntryPoints(state, detail.entry_points ?? []))
    .with({ name: P.union('queued', 'fetching', 'generating', 'triaging') }, () => state)
    .exhaustive()

/** Applies one event. Events at or before the run's current `seq` are replays and change nothing. */
export const fold = (state: BeaconRunState, event: BeaconEvent): BeaconRunState => {
  if (event.seq <= state.seq) {
    return state
  }
  const next = applyEvent(state, event)
  return {
    ...next,
    // The run's clock starts at its first frame — queued, for a run the platform had to wait to start.
    startTs: next.startTs ?? event.ts,
    seq: event.seq,
    ts: event.ts,
    phase: event.status,
    log: QUIET_EVENTS.has(event.name)
      ? next.log
      : [
          ...next.log,
          {
            seq: event.seq,
            ts: event.ts,
            kind: logKind(event),
            message: event.message,
            isFindingEvent: isFindingEvent(event),
          },
        ],
  }
}

/** Folds raw stream frames, skipping any whose name is unknown or whose detail is malformed. */
export const foldFrames = (state: BeaconRunState, frames: readonly BeaconStreamFrame[]): BeaconRunState =>
  frames.reduce((acc, frame) => {
    const event = parseBeaconEvent(frame)
    return event ? fold(acc, event) : acc
  }, state)

/** The run as it stood after the first `count` frames, rebuilt from nothing. */
export const replay = (frames: readonly BeaconStreamFrame[], count = frames.length): BeaconRunState =>
  foldFrames(EMPTY_BEACON_RUN, frames.slice(0, count))

// #region Selectors

export type Tone = 'finding' | 'candidate' | 'refuted'

export const candidateTone = (state: BeaconRunState, uuids: readonly string[]): Tone | undefined => {
  const states = uuids.map(uuid => state.candidates[uuid]?.state)
  if (states.includes('kept')) {
    return 'finding'
  }
  if (states.includes('suspected') || states.includes('upheld')) {
    return 'candidate'
  }
  return states.length > 0 ? 'refuted' : undefined
}

export type NodeState = 'mapped' | 'investigating' | 'examined' | 'candidate' | 'finding'

export const operationState = (state: BeaconRunState, operation: OperationNode): NodeState => {
  const tone = candidateTone(state, operation.candidates)
  if (tone === 'finding' || tone === 'candidate') {
    return tone
  }
  if (operation.active > 0) {
    return 'investigating'
  }
  return operation.isExamined ? 'examined' : 'mapped'
}

export const groupCandidates = (state: BeaconRunState, group: GroupNode): string[] => [
  ...group.candidates,
  ...group.opIds.flatMap(id => state.operations[id]?.candidates ?? []),
]

export const groupState = (state: BeaconRunState, group: GroupNode): NodeState => {
  const tone = candidateTone(state, groupCandidates(state, group))
  if (tone === 'finding' || tone === 'candidate') {
    return tone
  }
  const operations = group.opIds.flatMap(id => state.operations[id] ?? [])
  if (operations.some(operation => operation.active > 0)) {
    return 'investigating'
  }
  return operations.length > 0 && operations.every(operation => operation.isExamined) ? 'examined' : 'mapped'
}

export const keptIn = (state: BeaconRunState, uuids: readonly string[]): number =>
  uuids.filter(uuid => state.candidates[uuid]?.state === 'kept').length

export const suspectedIn = (state: BeaconRunState, uuids: readonly string[]): number =>
  uuids.filter(uuid => {
    const candidateState = state.candidates[uuid]?.state
    return candidateState === 'suspected' || candidateState === 'upheld'
  }).length

export const entryPointCandidates = (state: BeaconRunState, ep: EntryPointNode): string[] => [
  ...ep.candidates,
  ...ep.groupKeys.flatMap(key => state.groups[key]?.candidates ?? []),
  ...ep.opIds.flatMap(id => state.operations[id]?.candidates ?? []),
]

export const keptTotal = (state: BeaconRunState): number => keptIn(state, state.candidateOrder)

export const currentStage = (state: BeaconRunState): BeaconStage | undefined =>
  [...STAGES].reverse().find(({ stage }) => state.stages[stage].state !== 'pending')?.stage

export const isRunFinished = (state: Pick<BeaconRunState, 'status'>): boolean =>
  state.status === 'completed' || state.status === 'failed' || state.status === 'cancelled'

/**
 * The folded run, its status the platform's once the platform has finished the run. The platform can
 * finish a run with no terminal frame on its stream (one cancelled while queued, one analysis-service
 * lost), and once it has, its state is the run's.
 */
export const withPlatformOutcome = (state: BeaconRunState, run: BeaconRun | undefined): BeaconRunState =>
  match(run)
    .returnType<BeaconRunState>()
    .with({ state: 'completed' }, () => (state.status === 'completed' ? state : { ...state, status: 'completed' }))
    .with({ state: 'failed' }, ({ failure_reason }) =>
      state.status === 'failed'
        ? state
        : { ...state, status: 'failed', failureMessage: state.failureMessage ?? failure_reason }
    )
    .with({ state: 'cancelled' }, () => (state.status === 'cancelled' ? state : { ...state, status: 'cancelled' }))
    .otherwise(() => state)

export type FooterStats = {
  operations: number
  investigationsDone: number
  investigationsTotal: number
  candidatesRaw: number
  candidatesDeduped?: number
  kept: number
  fileReads: number
}

export const footerStats = (state: BeaconRunState): FooterStats => ({
  operations: Object.keys(state.operations).length,
  investigationsDone: state.investigationsDone,
  investigationsTotal: state.investigationOrder.length,
  candidatesRaw: state.candidatesRaw,
  candidatesDeduped: state.deduped,
  kept: keptTotal(state),
  fileReads: state.fileReads,
})

/**
 * The same footer, read from the stored summary. The live view's last frame and a finished run's
 * summary describe one run, so the two must agree; the run reducer's tests hold them to it.
 */
export const footerStatsFromSummary = (summary: BeaconSummary): Omit<FooterStats, 'fileReads'> => ({
  operations: summary.counts.operations,
  investigationsDone: summary.coverage.proof.investigations_run,
  investigationsTotal: summary.coverage.proof.investigations_planned,
  candidatesRaw: summary.coverage.proof.candidates_raw,
  candidatesDeduped: summary.coverage.proof.candidates_deduped,
  kept: summary.counts.findings_kept,
})

/**
 * The verdict's level as a reader sees it. analysis-service rates a run `on` when nothing alarmed, even
 * when part of the attack surface went unexamined; only a run that examined all of it reads as complete.
 */
export const displayedVerdictLevel = (
  verdict: Pick<CoverageVerdict, 'level'>,
  examinedPercent: number | undefined
): CoverageVerdictLevel => (verdict.level === 'on' && (examinedPercent ?? 0) < 100 ? 'degraded' : verdict.level)

// #endregion

// #region Pill

export type PillTone = 'pending' | 'running' | 'done' | 'stopped'
export type BeaconPill = {
  label: string
  tone: PillTone
  /** 0–1, how far the current step has got. */ fraction: number
}

type PillInput = {
  status: RunStatus
  phase: string
  stage?: BeaconStage | null
  /** Whether any Beacon stage has started; before the first one analysis-service builds the threat model. */
  hasStarted: boolean
  hasPlan: boolean
  lensesDone: number
  investigationsDone: number
  investigationsTotal?: number | null
  /** Candidates through verification and triage, as analysis-service counts them. */
  verified: number
  verifyTotal?: number | null
  kept: number
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

/** The run's one-line status, the same whether it is read from the stream or from the platform's snapshot. */
export const beaconPill = (input: PillInput): BeaconPill =>
  match(input)
    .with({ status: 'completed' }, ({ kept }) => ({
      label: `Completed · ${plural(kept, 'finding', 'findings')}`,
      tone: 'done' as const,
      fraction: 1,
    }))
    .with({ status: 'failed' }, () => ({ label: 'Failed', tone: 'stopped' as const, fraction: 0 }))
    .with({ status: 'cancelled' }, () => ({ label: 'Cancelled', tone: 'stopped' as const, fraction: 0 }))
    .with({ stage: 'verify' }, ({ verified, verifyTotal }) => ({
      label: `Verifying · ${verified} of ${verifyTotal ?? '…'}`,
      tone: 'running' as const,
      fraction: verifyTotal ? verified / verifyTotal : 0,
    }))
    .with({ phase: 'fetching' }, () => ({ label: 'Fetching code', tone: 'pending' as const, fraction: 0 }))
    .with({ phase: 'queued' }, () => ({ label: 'Queued', tone: 'pending' as const, fraction: 0 }))
    .with(P.union({ hasStarted: false }, { stage: 'threat_model' }), () => ({
      label: 'Building threat model',
      tone: 'running' as const,
      fraction: 0,
    }))
    .with({ hasPlan: false }, () => ({ label: 'Mapping attack surface', tone: 'running' as const, fraction: 0 }))
    .otherwise(({ lensesDone, investigationsDone, investigationsTotal }) => ({
      label: `Scanning · ${lensesDone} of ${DETECTION_LENSES.length} lenses`,
      tone: 'running' as const,
      fraction: investigationsTotal ? investigationsDone / investigationsTotal : 0,
    }))

export const pillFromState = (state: BeaconRunState): BeaconPill =>
  beaconPill({
    status: state.status,
    phase: state.phase,
    stage: state.stages.verify.state === 'running' ? 'verify' : currentStage(state),
    hasStarted: currentStage(state) !== undefined,
    hasPlan: state.investigationOrder.length > 0,
    lensesDone: state.lensesDone,
    investigationsDone: state.investigationsDone,
    investigationsTotal: state.investigationOrder.length,
    // Counted on triage, not on the verifier's rulings, so the pill reads as the platform's snapshot does.
    verified: state.triaged,
    verifyTotal: state.deduped,
    kept: keptTotal(state),
  })

const WAITING_PILL = { tone: 'pending', fraction: 0 } as const

const RUN_STATUS_BY_STATE = {
  'in-progress': 'running',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
} as const satisfies Record<Exclude<AiScanRunState, 'queued' | 'dispatching'>, RunStatus>

/** What analysis-service last reported about the run; a run the platform still holds has nothing yet. */
export const runProgressOf = (run: BeaconRun): BeaconProgress | undefined =>
  'progress' in run ? run.progress : undefined

/**
 * How many findings the run verified: the findings on a completed run's scan. Until verification ends,
 * `progress.findings_kept` counts candidates that verification has not yet rejected, so a running, failed or
 * cancelled run has none.
 */
export const verifiedFindingsOf = (run: BeaconRun): number | undefined =>
  run.state === 'completed' ? run.findings_count : undefined

/**
 * The pill read from the platform's run snapshot. Before analysis-service accepts a run there is no progress
 * to read, so the platform's own states name it; from then on it reads like {@link pillFromState}.
 */
export const pillFromRun = (run: BeaconRun): BeaconPill =>
  match(run)
    .with({ state: 'queued', retry_after: P.string }, () => ({ ...WAITING_PILL, label: 'Waiting for capacity' }))
    .with({ state: 'queued' }, () => ({ ...WAITING_PILL, label: 'Queued' }))
    .with({ state: 'dispatching' }, () => ({ ...WAITING_PILL, label: 'Starting' }))
    .with({ state: P.union('in-progress', 'completed', 'failed', 'cancelled') }, finished => {
      const { progress } = finished
      return beaconPill({
        status: RUN_STATUS_BY_STATE[finished.state],
        phase: progress?.phase ?? 'queued',
        stage: progress?.stage,
        hasStarted: progress?.stage !== undefined,
        hasPlan: (progress?.investigations?.total ?? 0) > 0,
        lensesDone: progress?.lenses?.done ?? 0,
        investigationsDone: progress?.investigations?.done ?? 0,
        investigationsTotal: progress?.investigations?.total,
        verified: progress?.verification?.done ?? 0,
        verifyTotal: progress?.verification?.total,
        kept: verifiedFindingsOf(finished) ?? 0,
      })
    })
    .exhaustive()

// #endregion
